<template>
  <model-form
    :model="reportSchedule"
    modal-type="modal"
  >
    <div class="column">
      <div class="row q-gutter-md items-start">
        <q-input
          v-model="reportSchedule.name"
          class="col"
          label="Name"
          autofocus
          bottom-slots
          :rules="[val => !!val || 'Field is required']"
        />
      </div>
      <div class="row q-gutter-md items-center">
        <q-select
          v-model="reportSchedule.report"
          class="col"
          label="Report"
          option-value="id"
          option-label="name"
          map-options
          :options="reports.data"
          input-debounce="300"
          input-style="search"
          bottom-slots
          use-input
          @filter="filterReports"
        >
          <template #option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section>
                <q-item-label>{{ scope.opt.name }}</q-item-label>
                <q-item-label
                  v-if="scope.opt.reportCategory"
                  caption
                >
                  {{ scope.opt.reportCategory.name }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>
      </div>
      <div class="row q-gutter-col-md items-center">
        <q-checkbox
          v-model="reportSchedule.includeCsv"
          class="col-3"
          label="Include CSV"
        >
          <q-tooltip>
            Include a CSV with raw reporting data in emailed reports for richer data inspection.
          </q-tooltip>
        </q-checkbox>
        <q-checkbox
          v-model="reportSchedule.sendSeparateEmails"
          class="col-4"
          label="Separate Emails"
        >
          <q-tooltip>
            Send individual emails to each given address,
            or include all address in the to line of a single email.
          </q-tooltip>
        </q-checkbox>
        <div class="col-2" />
        <q-checkbox
          v-model="reportSchedule.enabled"
          label="Enabled"
          class="col-3"
        />
      </div>
      <div class="row items-center q-mb-md">
        <q-select
          v-model="emails"
          class="col"
          label="Emails"
          use-input
          new-value-mode="add-unique"
          hint="Press enter to add an address"
          multiple
          clearable
          use-chips
          bottom-slots
          :rules="[_value => (!!emails && emails.length > 0) || 'Must include at least one email']"
        />
      </div>
      <div
        class="row q-col-gutter-md items-center"
      >
        <q-toggle
          :model-value="reportSchedule.daysOfWeek !== null"
          class="col-6 q-mb-md"
          label="Specific Days of Week"
          bottom-slots
          @update:model-value="toggleDaysOfWeek"
        />
        <q-select
          v-model="reportSchedule.daysOfWeek"
          class="col-6"
          label="Days of Week"
          :options="DAYS_OF_WEEK_FOR_SELECT"
          map-options
          emit-value
          multiple
          use-chips
          :disable="reportSchedule.daysOfWeek === null"
          :rules="[_value => reportSchedule.daysOfWeek === null || reportSchedule.daysOfWeek.length > 0 || 'Must select at least one day']"
        />
      </div>
      <div
        class="row q-col-gutter-md items-center"
      >
        <q-toggle
          :model-value="reportSchedule.daysOfMonth !== null"
          class="col-6 q-mb-md"
          label="Specific Days of Month"
          bottom-slots
          @update:model-value="toggleDaysOfMonth"
        />
        <q-select
          v-model="reportSchedule.daysOfMonth"
          class="col-6"
          label="Days of Month"
          :options="$_.range(1,29)"
          multiple
          use-chips
          :disable="reportSchedule.daysOfMonth === null"
          :rules="[_value => reportSchedule.daysOfMonth === null || reportSchedule.daysOfMonth.length > 0 || 'Must select at least one day']"
        />
      </div>
      <div
        class="row q-col-gutter-md items-center"
      >
        <q-toggle
          :model-value="reportSchedule.monthsOfYear !== null"
          class="col-6 q-mb-md"
          label="Specific Months of the Year"
          bottom-slots
          @update:model-value="toggleMonthsOfYear"
        />
        <q-select
          v-model="reportSchedule.monthsOfYear"
          class="col-6"
          label="Months of the Year"
          :options="$_.range(1,13)"
          multiple
          use-chips
          :disable="reportSchedule.monthsOfYear === null"
          :rules="[_value => reportSchedule.monthsOfYear === null || reportSchedule.monthsOfYear.length > 0 || 'Must select at least one day']"
        />
      </div>
      <div
        class="row q-col-gutter-md items-center"
      >
        <div class="col-6" />
        <q-select
          v-model="reportSchedule.hourOfDay"
          class="col-6"
          label="Hour of Day"
          :options="$_.range(0, 24)"
          :rules="[_value => reportSchedule.hourOfDay !== null || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            Hour of day in military time. e.g. 0 is midnight and 12 is noon, and 16 is 4 pm
          </q-tooltip>
        </q-select>
      </div>
    </div>
  </model-form>
</template>
<script>
import { DAYS_OF_WEEK_FOR_SELECT } from 'src/models/report/ReportSchedule'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

export default {
  name: 'ReportScheduleForm',
  components: {
    ModelForm
  },
  data () {
    return {
      searchString: '',
      emails: []
    }
  },
  watch: {
    'reportSchedule.id' () {
      this.emails = this.reportSchedule.emails ? this.reportSchedule.emails.split(',') : []
    },
    emails () {
      this.reportSchedule.emails = this.emails.join(',')
    }
  },
  models: {
    reportSchedule: {
      report: 'default'
    },
    reports: {
      fields: ['id', 'name', 'reportCategory'],
      variables () {
        return {
          searchString: this.searchString,
          limit: 100
        }
      }
    }
  },
  created () {
    _.extend(this, { DAYS_OF_WEEK_FOR_SELECT })
  },
  methods: {
    async filterReports (val, update) {
      update(() => {
        this.searchString = val
      })
    },
    toggleDaysOfWeek (toggleValue) {
      if (toggleValue) {
        this.reportSchedule.monthsOfYear = null
        this.reportSchedule.daysOfMonth = null
        this.reportSchedule.daysOfWeek = []
      } else {
        this.reportSchedule.daysOfWeek = null
      }
    },
    toggleDaysOfMonth (toggleValue) {
      if (toggleValue) {
        this.reportSchedule.daysOfWeek = null
        this.reportSchedule.daysOfMonth = []
      } else {
        this.reportSchedule.monthsOfYear = null
        this.reportSchedule.daysOfMonth = null
      }
    },
    toggleMonthsOfYear (toggleValue) {
      if (toggleValue) {
        this.reportSchedule.daysOfWeek = null
        this.reportSchedule.daysOfMonth = []
        this.reportSchedule.monthsOfYear = []
      } else {
        this.reportSchedule.monthsOfYear = null
      }
    }
  }
}

</script>
